import {type ApolloError} from '@apollo/client';
import {useMemo} from 'react';

import {useHistoryKeyInfoQueryVars} from '../../state/runs/hooks';
import type * as CustomRunNamesTypes from '../../state/views/customRunNames/types';
import type * as RunSetTypes from '../../state/views/runSet/types';
import {useNgwTruncatedPanelGenLimits} from '../../util/dynamicConfigs/ngwTruncatedPanelGenLimits';
import {extractMetricSearchErrorMessage} from '../../util/errors';
import * as QueryTS from '../../util/queryts';
import {useLatchedRejectionMessage} from '../../util/useInvalidRunFilter';
import {useMetricSearch} from '../../util/useMetricSearch';

const DEFAULT_PAGE_SIZE = 100;

export type MetricKeySearchResult = {
  key: string;
  types: string[];
};

const EMPTY_RESULTS: MetricKeySearchResult[] = [];

export type UseMetricKeySearchOptions = {
  /**
   * Any-match membership on `MetricInfo.types` (e.g. `['number']` for the
   * x-axis, `['number', 'histogram']` for the y-axis). Applied by the backend
   * via the `metricTypes` arg.
   */
  types?: string[];
  metricNamePattern?: string;
  pageSize?: number;
  skip?: boolean;
};

export function useMetricKeySearch(
  runSetRefs: RunSetTypes.Ref[],
  customRunNamesRef: CustomRunNamesTypes.Ref | undefined,
  {
    types,
    metricNamePattern,
    pageSize = DEFAULT_PAGE_SIZE,
    skip = false,
  }: UseMetricKeySearchOptions = {}
): {
  results: MetricKeySearchResult[];
  hasMore: boolean;
  loading: boolean;
  error: ApolloError | undefined;
  /**
   * The backend's message when it rejected `metricNamePattern` as a regex it
   * can't run (RE2 has no lookahead, lookbehind, or backreference). The user
   * fixes this by editing the pattern, so it is reported separately from
   * `error`, which stays reserved for a failure a retry could clear.
   */
  searchErrorMessage: string | undefined;
  retry: () => void;
} {
  const vars = useHistoryKeyInfoQueryVars(runSetRefs, customRunNamesRef);
  const {
    config: {runLimit},
  } = useNgwTruncatedPanelGenLimits();

  const {data, loading, error, retry} = useMetricSearch({
    entityName: vars.entityName,
    projectName: vars.projectName,
    filters: vars.filters,
    metricNamePattern,
    pageSize,
    requiredRunKeys: vars.requiredRunKeys,
    runLimit,
    runOrder: QueryTS.sortToOrderString(vars.sort),
    types,
    skip,
  });

  const results = useMemo<MetricKeySearchResult[]>(() => {
    const metrics = data?.metrics;
    if (metrics == null || metrics.length === 0) {
      return EMPTY_RESULTS;
    }
    return metrics;
  }, [data]);

  // Latched on the pattern: the rejection has to survive the polls and
  // refetches that follow it, and only stops applying once the pattern changes.
  const searchErrorMessage = useLatchedRejectionMessage(
    extractMetricSearchErrorMessage(error),
    metricNamePattern ?? ''
  );

  return {
    results,
    hasMore: data?.hasMore ?? false,
    loading,
    // A rejected pattern must not reach consumers as a load error: they render
    // it with a retry that can't clear it, and useRegexMetricsSync would read
    // the empty result as "the regex matches nothing".
    error: searchErrorMessage == null ? error : undefined,
    searchErrorMessage,
    retry,
  };
}
